import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SearchFilter from './SearchFilter';
import { getSearchFilters } from '../../services/apiService';
import SETTING_KEYS from '../../constants/setting-keys';
import styles from './ResultFilters.module.css';

const ResultFilter: React.FC<{
    term: string;
    defaultActiveFilters: { [key: string]: string[] };
    onChange: (filt: { [key: string]: string[] }) => void;
}> = ({ term, defaultActiveFilters, onChange }) => {
    const { t } = useTranslation();
    const [manufacturers, setManufacturers] = useState([]);
    const [selectedManufacturers, setSelectedManufacturers] = useState(
        defaultActiveFilters[SETTING_KEYS.manufacturer] || []
    );

    const [types, setTypes] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState(
        defaultActiveFilters[SETTING_KEYS.type] || []
    );

    const [bodyPlacement, setBodyPlacement] = useState([]);
    const [selectedBodyPlacement, setSelectedBodyPlacement] = useState(
        defaultActiveFilters[SETTING_KEYS.bodyPlacement] || []
    );

    const [regions, setRegions] = useState([]);
    const [selectedRegions, setSelectedRegions] = useState(
        defaultActiveFilters.region || []
    );

    const fetchFilters = async () => {
        const filters = await getSearchFilters(term, {
            [SETTING_KEYS.type]: selectedTypes,
            [SETTING_KEYS.manufacturer]: selectedManufacturers,
            [SETTING_KEYS.bodyPlacement]: selectedBodyPlacement,
            regions: selectedRegions,
        });

        setRegions(filters.regions);
        setManufacturers(filters[SETTING_KEYS.manufacturer]);
        setTypes(filters[SETTING_KEYS.type]);
        setBodyPlacement(filters[SETTING_KEYS.bodyPlacement]);
    };

    useEffect(() => {
        fetchFilters();
    }, [term, selectedManufacturers, selectedTypes, selectedBodyPlacement]);

    const handleManufacturerChange = (value: string[]) => {
        onChange({
            [SETTING_KEYS.bodyPlacement]: selectedBodyPlacement,
            [SETTING_KEYS.type]: selectedTypes,
            [SETTING_KEYS.manufacturer]: value,
            regions: selectedRegions,
        });
        setSelectedManufacturers(value);
    };

    const handleTypeChange = (value: string[]) => {
        onChange({
            [SETTING_KEYS.type]: value,
            [SETTING_KEYS.manufacturer]: selectedManufacturers,
            [SETTING_KEYS.bodyPlacement]: selectedBodyPlacement,
            regions: selectedRegions,
        });
        setSelectedTypes(value);
    };

    const handleBodyPlacementChange = (value: string[]) => {
        onChange({
            [SETTING_KEYS.bodyPlacement]: value,
            [SETTING_KEYS.manufacturer]: selectedManufacturers,
            [SETTING_KEYS.type]: selectedTypes,
            regions: selectedRegions,
        });
        setSelectedBodyPlacement(value);
    };

    const handleRegionChange = (value: string[]) => {
        onChange({
            regions: value,
            [SETTING_KEYS.manufacturer]: selectedManufacturers,
            [SETTING_KEYS.type]: selectedTypes,
            [SETTING_KEYS.bodyPlacement]: selectedBodyPlacement,
        });
        setSelectedRegions(value);
    };

    return (
        <div className={styles.wrapper}>
            <h2>{t('search.resultFilters.title')}</h2>
            <p>{t('search.resultFilters.description')}</p>
            {/* TODO(sixten): How do we default the filter to user's region */}
            <SearchFilter
                title={t('search.resultFilters.filters.regions')}
                values={regions}
                defaultSelected={selectedRegions}
                onChange={handleRegionChange}
            />
            <SearchFilter
                title={t('search.resultFilters.filters.manufacturers')}
                values={manufacturers}
                defaultSelected={selectedManufacturers}
                onChange={handleManufacturerChange}
            />
            <SearchFilter
                title={t('search.resultFilters.filters.types')}
                values={types}
                defaultSelected={selectedTypes}
                onChange={handleTypeChange}
            />
            <SearchFilter
                title={t('search.resultFilters.filters.bodyPlacement')}
                values={bodyPlacement}
                defaultSelected={selectedBodyPlacement}
                onChange={handleBodyPlacementChange}
            />
        </div>
    );
};

export default ResultFilter;
